import { omit } from 'lodash';
import { LOGGED_IN_COOKIE } from './constants';

export const getCookie = (name) => {
  const decodedCookie = decodeURIComponent(document.cookie);
  const allCookies = decodedCookie.split(';');
  const matchingCookie = allCookies.find((cookie) => {
    const cookieName = cookie.trim().split('=')[0];
    return cookieName === name;
  });

  return matchingCookie ? matchingCookie.split('=')[1] : null;
};

export const isUserAuthenticated = () => {
  const cookie = getCookie(LOGGED_IN_COOKIE);
  return cookie === '1';
};

export const setCookie = (name, value, maxAgeSeconds = 60 * 60 * 24 * 365) => {
  if (!name) {
    return;
  }
  document.cookie = `${name}=${value};max-age=${maxAgeSeconds}`;
};

export function deleteCookie(name) {
  document.cookie = `${name}=; Max-Age=0`;
}

export const getRandomArbitrary = (min, max) => {
  return Math.random() * (max - min) + min;
};

export function formatNumberWithIntl(number) {
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
  });

  if (number >= 1e9) {
    return `${formatter.format(number / 1e9)}B`;
  }

  if (number >= 1e6) {
    return `${formatter.format(number / 1e6)}M`;
  }

  if (number >= 1000) {
    return `${formatter.format(number / 1000)}k`;
  }

  return formatter.format(number);
}

export function getPercentChange(previous, current) {
  if (!previous) {
    return '∞';
  }

  const roundedValue = (((current - previous) / previous) * 100).toFixed(1);
  const isChanged = current - previous !== 0;
  const isNegative = roundedValue < 0;
  const sign = roundedValue > 0 ? '↑' : '↓';

  return isChanged
    ? `${!isChanged ? '' : sign}${
        isNegative ? roundedValue.slice(1) : roundedValue
      }%`
    : '0%';
}

export function sumJourneysByKey(key, collection) {
  return collection.reduce((acc, entry) => {
    const value = entry[key];

    return {
      ...acc,
      [value]: {
        ...acc[value],
        [key]: value,
        mobile_journeys:
          (acc[value]?.mobile_journeys || 0) + entry.mobile_journeys,
      },
    };
  }, {});
}

export function createChartDataByKey({
  currentData,
  previousData,
  key,
  numberOfTopItems = 5,
}) {
  const currentTotalJourneys = currentData.reduce(
    (acc, entry) => acc + entry.mobile_journeys,
    0,
  );
  const currentJourneysByKey = sumJourneysByKey(key, currentData);
  const currentSortedJourneysByKey = Object.values(currentJourneysByKey).sort(
    (a, b) => b.mobile_journeys - a.mobile_journeys,
  );
  const previousJourneysByKey = sumJourneysByKey(key, previousData);
  const previousTotalJourneys = previousData.reduce(
    (acc, entry) => acc + entry.mobile_journeys,
    0,
  );
  const currentTop = numberOfTopItems
    ? currentSortedJourneysByKey.slice(0, numberOfTopItems)
    : currentSortedJourneysByKey;
  const currentOther = numberOfTopItems
    ? currentSortedJourneysByKey.slice(numberOfTopItems)
    : [];
  const currentOtherTotalJourneys = currentOther.reduce(
    (acc, stat) => acc + stat.mobile_journeys,
    0,
  );
  const previousOther = omit(
    previousJourneysByKey,
    currentTop.map((c) => c[key]),
  );
  const previousOtherTotalJourneys = Object.values(previousOther).reduce(
    (acc, stat) => acc + stat.mobile_journeys,
    0,
  );
  const labels = [
    ...currentTop.map((c) => c[key]),
    ...(currentOther.length ? ['Other'] : []),
  ];
  const series = [
    ...currentTop.map((stat) => stat.mobile_journeys),
    ...(currentOther.length ? [currentOtherTotalJourneys] : []),
  ];
  const legend = labels.map((value, index) => {
    const currentJourneys =
      value === 'Other' ? currentOtherTotalJourneys : series[index];
    const previousJourneys =
      value === 'Other'
        ? previousOtherTotalJourneys
        : previousJourneysByKey[value]?.mobile_journeys;
    const percentage = `${(
      (currentJourneys / currentTotalJourneys) *
      100
    ).toFixed(1)}%`;

    return {
      [key]: value,
      journeys: series[index],
      previousJourneys,
      currentJourneys,
      percentage,
    };
  });

  return {
    labels,
    series,
    legend,
    currentJourneysByKey,
    currentTop,
    currentOther,
    currentTotalJourneys,
    previousJourneysByKey,
    previousTotalJourneys,
    previousOtherTotalJourneys,
  };
}
